/**
 * AI Agent React Query Hooks
 *
 * Provides React Query hooks for AI Agent data fetching and mutations.
 * Includes proper cache invalidation.
 */

'use client'

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { agentService } from '../api/services/agents'
import type {
  Agent,
  AgentDetail,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentSearchParams,
  PaginatedResponse,
  ExecuteAgentRequest,
  ExecutionResult,
} from '../api/types'

// ============================================
// Query Keys
// ============================================

export const agentKeys = {
  all: ['agents'] as const,
  lists: () => [...agentKeys.all, 'list'] as const,
  list: (params?: AgentSearchParams) => [...agentKeys.lists(), params] as const,
  activeList: () => [...agentKeys.all, 'active'] as const,
  details: () => [...agentKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentKeys.details(), id] as const,
  bySlug: (slug: string) => [...agentKeys.all, 'slug', slug] as const,
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch agents with optional filtering and pagination
 */
export function useAgents(
  params?: AgentSearchParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<Agent>, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: agentKeys.list(params),
    queryFn: () => agentService.getAgents(params),
    ...options,
  })
}

/**
 * Hook to fetch only active agents (for dropdowns, selections)
 */
export function useActiveAgents(
  options?: Omit<UseQueryOptions<Agent[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: agentKeys.activeList(),
    queryFn: () => agentService.getActiveAgents(),
    ...options,
  })
}

/**
 * Hook to fetch a single agent by ID
 */
export function useAgent(
  id: string,
  options?: Omit<UseQueryOptions<AgentDetail, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: agentKeys.detail(id),
    queryFn: () => agentService.getAgent(id),
    enabled: !!id,
    ...options,
  })
}

/**
 * Hook to fetch a single agent by slug
 */
export function useAgentBySlug(
  slug: string,
  includeCapabilities = true,
  options?: Omit<UseQueryOptions<AgentDetail, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: agentKeys.bySlug(slug),
    queryFn: () => agentService.getAgentBySlug(slug, includeCapabilities),
    enabled: !!slug,
    ...options,
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new agent
 */
export function useCreateAgent(
  options?: UseMutationOptions<AgentDetail, Error, CreateAgentRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAgentRequest) => agentService.createAgent(data),
    onSuccess: () => {
      // Invalidate agent list queries to refetch
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentKeys.activeList() })
    },
    ...options,
  })
}

/**
 * Hook to update an existing agent
 */
export function useUpdateAgent(
  options?: UseMutationOptions<AgentDetail, Error, { id: string; data: UpdateAgentRequest }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => agentService.updateAgent(id, data),
    onSuccess: (updatedAgent, variables) => {
      // Update the agent in cache
      queryClient.setQueryData(agentKeys.detail(variables.id), updatedAgent)

      // Also update by slug if available
      if (updatedAgent.slug) {
        queryClient.setQueryData(agentKeys.bySlug(updatedAgent.slug), updatedAgent)
      }

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentKeys.activeList() })
    },
    ...options,
  })
}

/**
 * Hook to delete an agent
 */
export function useDeleteAgent(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agentService.deleteAgent(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: agentKeys.detail(id) })

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentKeys.activeList() })
    },
    ...options,
  })
}

/**
 * Hook to activate an agent
 */
export function useActivateAgent(
  options?: UseMutationOptions<Agent, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agentService.activateAgent(id),
    onSuccess: (updatedAgent, id) => {
      // Update the agent in cache
      queryClient.setQueryData(agentKeys.detail(id), updatedAgent)

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentKeys.activeList() })
    },
    ...options,
  })
}

/**
 * Hook to deactivate an agent
 */
export function useDeactivateAgent(
  options?: UseMutationOptions<Agent, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agentService.deactivateAgent(id),
    onSuccess: (updatedAgent, id) => {
      // Update the agent in cache
      queryClient.setQueryData(agentKeys.detail(id), updatedAgent)

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentKeys.activeList() })
    },
    ...options,
  })
}

/**
 * Hook to test an agent's configuration
 */
export function useTestAgent(
  options?: UseMutationOptions<
    { response: string; usage: { inputTokens: number; outputTokens: number } },
    Error,
    { id: string; testPrompt: string }
  >
) {
  return useMutation({
    mutationFn: ({ id, testPrompt }) => agentService.testAgent(id, testPrompt),
    ...options,
  })
}

// ============================================
// Execution Hooks
// ============================================

/**
 * Hook to execute an agent (agent decides which skill to use)
 */
export function useExecuteAgent(
  options?: UseMutationOptions<
    ExecutionResult,
    Error,
    { id: string; request: ExecuteAgentRequest }
  >
) {
  return useMutation({
    mutationFn: ({ id, request }) => agentService.executeAgent(id, request),
    ...options,
  })
}

/**
 * Hook to execute a specific skill for an agent
 */
export function useExecuteSkill(
  options?: UseMutationOptions<
    ExecutionResult,
    Error,
    { agentId: string; skillSlug: string; request: ExecuteAgentRequest }
  >
) {
  return useMutation({
    mutationFn: ({ agentId, skillSlug, request }) =>
      agentService.executeSkill(agentId, skillSlug, request),
    ...options,
  })
}

/**
 * Hook to get agent capabilities/skills (uses agent detail query)
 * Returns just the capabilities from the agent detail
 */
export function useAgentCapabilities(
  agentId: string,
  options?: Omit<UseQueryOptions<AgentDetail, Error>, 'queryKey' | 'queryFn' | 'select'>
) {
  return useQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => agentService.getAgent(agentId),
    enabled: !!agentId,
    select: (data) => data.capabilities,
    ...options,
  })
}
